/**
 * Shared Question Editor Component
 * - Used by CreateExam (per-question) and Questions page modal/editor
 * - When showSubjectClass=false, subject/class selectors are hidden and values can be defaulted via props
 */
import React, { useRef, useState, useEffect } from 'react';
import { Question, QuestionType, Answer, ExamCategory } from '../types';
import { imageApi } from '../services/api';
import PreviewImageModal from './PreviewImageModal';
import { SHOW_GEOMETRIC_SHAPES } from '../config/uiFlags';

export type QuestionEditorProps = {
    question: Question;
    onChange: (patch: Partial<Question>) => void;
    // Dropdowns
    showSubjectClass: boolean;
    // Show complexity and tags (CreateExam context needs this true)
    showComplexityTags?: boolean;
    // Show/hide the shapes editor tools (requested to hide on Questions page)
    showShapesEditor?: boolean;
    categories: ExamCategory[];
    classes: string[];
    // Defaults when subject/class are hidden (Create Exam context)
    defaultSubject?: string; // category name
    defaultClassLevel?: string;
};

const QuestionEditor: React.FC<QuestionEditorProps> = ({
    question,
    onChange,
    showSubjectClass,
    showComplexityTags = false,
    // default controlled by global UI flag
    showShapesEditor = SHOW_GEOMETRIC_SHAPES,
    categories,
    classes,
    defaultSubject,
    defaultClassLevel,
}) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    // Unique radio group name per QuestionEditor instance to prevent cross-question interference
    const radioGroupNameRef = useRef<string>('correct_' + Math.random().toString(36).slice(2));
    const selectionRef = useRef<Range | null>(null);
    const [tagInput, setTagInput] = useState('');
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);

    // --- KaTeX dynamic loader and helpers ---
    const katexReadyRef = useRef<Promise<void> | null>(null);
    const ensureKatex = (): Promise<void> => {
        if (katexReadyRef.current) return katexReadyRef.current;
        katexReadyRef.current = new Promise<void>((resolve) => {
            const w = window as any;
            const done = () => resolve();
            // CSS
            if (!document.getElementById('katex-css')) {
                const link = document.createElement('link');
                link.id = 'katex-css';
                link.rel = 'stylesheet';
                link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.10/dist/katex.min.css';
                document.head.appendChild(link);
            }
            // JS
            if (w.katex) { resolve(); return; }
            if (!document.getElementById('katex-js')) {
                const script = document.createElement('script');
                script.id = 'katex-js';
                script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.10/dist/katex.min.js';
                script.onload = () => done();
                script.onerror = () => done();
                document.body.appendChild(script);
            } else {
                // If script tag exists but not loaded yet, wait a tick
                setTimeout(() => done(), 50);
            }
        });
        return katexReadyRef.current;
    };

    const renderKatexIn = async (root: HTMLElement) => {
        await ensureKatex();
        const w = window as any;
        const nodes = Array.from(root.querySelectorAll('span.math-tex')) as HTMLElement[];
        nodes.forEach((span) => {
            const tex = span.getAttribute('data-tex') || span.textContent || '';
            try {
                w.katex && w.katex.render(tex, span, { throwOnError: false });
                // keep attribute for future editing
                span.setAttribute('data-tex', tex);
                span.classList.add('math-tex');
                // Make rendered math non-breaking within editable content
                span.contentEditable = 'false';
            } catch {
                // ignore rendering errors
            }
        });
    };

    const escapeHtml = (s: string) => s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const insertMath = async (tex: string) => {
        const el = editorRef.current;
        if (!el) return;
        // Focus editor and restore last known selection (before prompts)
        el.focus();
        restoreSelection();
        // If after restoring, selection is missing or outside the editor, move caret to end
        try {
            const sel = window.getSelection();
            const anchor = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).startContainer : null;
            if (!sel || sel.rangeCount === 0 || (anchor && !el.contains(anchor))) {
                const range = document.createRange();
                range.selectNodeContents(el);
                range.collapse(false);
                sel?.removeAllRanges();
                sel?.addRange(range);
            }
        } catch {}
        // Insert math + a temporary caret marker to deterministically place the caret after insertion
        const markerId = `caret-marker-${Date.now()}-${Math.floor(Math.random()*1e6)}`;
        const html = `<span class=\"math-tex\" data-tex=\"${escapeHtml(tex)}\">${escapeHtml(tex)}</span><span id=\"${markerId}\" data-caret=\"1\" style=\"display:inline-block;width:0;height:0;overflow:hidden;\">\uFEFF</span>`;
        document.execCommand('insertHTML', false, html);
        // Render newly inserted math
        await renderKatexIn(el);
        // Move selection to the marker and then remove it
        try {
            const marker = document.getElementById(markerId);
            if (marker) {
                const range = document.createRange();
                range.setStartAfter(marker);
                range.collapse(true);
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(range);
                marker.parentElement?.removeChild(marker);
            }
        } catch {}
    };

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) selectionRef.current = sel.getRangeAt(0);
    };
    const restoreSelection = () => {
        const sel = window.getSelection();
        if (selectionRef.current && sel) {
            sel.removeAllRanges();
            sel.addRange(selectionRef.current);
        }
    };

    // Bookmark-based caret preservation that survives window.prompt focus loss
    const placeCaretBookmark = (): string | null => {
        const el = editorRef.current;
        if (!el) return null;
        el.focus();
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        const range = sel.getRangeAt(0).cloneRange();
        const id = `caret-bm-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        const marker = document.createElement('span');
        marker.id = id;
        marker.setAttribute('data-caret-bm', '1');
        marker.style.display = 'inline-block';
        marker.style.width = '0';
        marker.style.height = '0';
        marker.style.overflow = 'hidden';
        marker.appendChild(document.createTextNode('\uFEFF'));
        try {
            range.insertNode(marker);
        } catch {}
        // Move selection to after marker so typing without restoration still works reasonably
        try {
            const after = document.createRange();
            after.setStartAfter(marker);
            after.collapse(true);
            sel.removeAllRanges();
            sel.addRange(after);
        } catch {}
        return id;
    };

    const restoreCaretFromBookmark = (id: string | null) => {
        const el = editorRef.current;
        if (!el || !id) return;
        const marker = document.getElementById(id);
        if (!marker || !el.contains(marker)) return;
        try {
            const sel = window.getSelection();
            const range = document.createRange();
            range.setStartBefore(marker);
            range.collapse(true);
            sel?.removeAllRanges();
            sel?.addRange(range);
            marker.parentElement?.removeChild(marker);
        } catch {
            // If anything fails, ensure caret at end of editor
            try {
                const sel = window.getSelection();
                const end = document.createRange();
                end.selectNodeContents(el);
                end.collapse(false);
                sel?.removeAllRanges();
                sel?.addRange(end);
            } catch {}
        }
    };

    useEffect(() => {
        // Load KaTeX once and render any existing math on mount
        const run = async () => {
            try {
                if (editorRef.current) {
                    await renderKatexIn(editorRef.current);
                }
            } catch {}
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Sync DOM content from prop and render KaTeX when external content changes
        const el = editorRef.current;
        if (!el) return;
        // Avoid clobbering user typing/selection: if editor is focused, skip syncing
        if (document.activeElement === el) return;
        const nextHtml = question.questionRichText || '';
        if (el.innerHTML !== nextHtml) {
            el.innerHTML = nextHtml;
            const t = setTimeout(() => { renderKatexIn(el); }, 0);
            return () => clearTimeout(t);
        }
        return;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question.questionRichText]);

    const insertTableAtSelection = (rows: number, cols: number) => {
        rows = Math.max(1, Math.min(20, Math.floor(rows)));
        cols = Math.max(1, Math.min(20, Math.floor(cols)));
        const cellStyle = "min-width:28px;padding:6px 8px;text-align:right;vertical-align:middle;border:1px solid #ccc;line-height:1.3;min-height:1.6em;font-size:inherit;";
        const cells = Array.from({ length: rows })
            .map(() => `<tr>${Array.from({ length: cols }).map(() => `<td style="${cellStyle}">&nbsp;</td>`).join('')}</tr>`)
            .join('');
        const html = `<table style="border-collapse:collapse;border:1px solid #999;margin:4px 0;">${cells}</table>`;
        document.execCommand('insertHTML', false, html);
    };

    const handleInsertTable = () => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        let sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        }
        const colsStr = window.prompt('How many columns (digits)?', '3');
        if (colsStr == null) return;
        const rowsStr = window.prompt('How many rows?', '2');
        if (rowsStr == null) return;
        const cols = parseInt(colsStr, 10);
        const rows = parseInt(rowsStr, 10);
        if (Number.isNaN(cols) || Number.isNaN(rows)) return;
        el.focus();
        sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        }
        insertTableAtSelection(rows, cols);
        // Move caret to first cell
        try {
            const tables = el.querySelectorAll('table');
            const lastTable = tables[tables.length - 1] as HTMLTableElement | undefined;
            const firstTd = lastTable?.querySelector('td') as HTMLElement | null | undefined;
            if (firstTd) {
                const r = document.createRange();
                r.selectNodeContents(firstTd);
                r.collapse(true);
                const s = window.getSelection();
                s?.removeAllRanges();
                s?.addRange(r);
            }
        } catch {}
        onChange({ questionRichText: el.innerHTML });
    };

    const handleToggleBorders = () => {
        const el = editorRef.current;
        if (!el) return;
        const tables = Array.from(el.querySelectorAll('table')) as HTMLTableElement[];
        if (tables.length === 0) return;
        const anyVisible = tables.some(t => (t.style.borderStyle !== 'none' && t.style.border !== '0px' && t.getAttribute('data-borders') !== 'hidden'));
        const makeHidden = anyVisible;
        tables.forEach(t => {
            const cells = Array.from(t.querySelectorAll('th,td')) as HTMLElement[];
            if (makeHidden) {
                t.style.border = '0';
                t.style.borderCollapse = 'collapse';
                t.setAttribute('data-borders', 'hidden');
                cells.forEach(c => { c.style.border = '0'; });
            } else {
                t.style.border = '1px solid #999';
                t.style.borderCollapse = 'collapse';
                t.removeAttribute('data-borders');
                cells.forEach(c => { c.style.border = '1px solid #ccc'; });
            }
        });
        onChange({ questionRichText: el.innerHTML });
    };

    const MAX_UPLOAD_BUTTON = 5 * 1024 * 1024; // 5MB
    const MAX_PASTE = 2 * 1024 * 1024; // 2MB

    const ALLOWED_MIME = new Set<string>(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']);
    const ALLOWED_LABEL = 'JPG, JPEG, PNG, WEBP, GIF';
    const isAllowedMime = (type?: string | null) => !!type && ALLOWED_MIME.has(type.toLowerCase());

    const uploadImage = async (file: File) => {
        if (!isAllowedMime(file.type)) {
            alert(`Only ${ALLOWED_LABEL} images are allowed`);
            return;
        }
        if (file.size > MAX_UPLOAD_BUTTON) {
            alert('Uploaded image must be under 5MB');
            return;
        }
        const { url } = await imageApi.upload(file, 'button');
        onChange({ imagePath: url });
    };

    // Convert data URL to File
    const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return new File([blob], filename, { type: blob.type || 'image/png' });
    };

    // Replace any <img src="data:"> inside the editor with uploaded URLs
    const replaceDataImagesInElement = async (el: HTMLDivElement) => {
        const images = Array.from(el.querySelectorAll('img')) as HTMLImageElement[];
        for (const img of images) {
            const src = img.getAttribute('src') || '';
            if (src.startsWith('data:image')) {
                try {
                    const file = await dataUrlToFile(src, `pasted-${Date.now()}.png`);
                    if (!isAllowedMime(file.type)) {
                        alert(`Only ${ALLOWED_LABEL} images are allowed`);
                        img.remove();
                        continue;
                    }
                    if (file.size > MAX_PASTE) {
                        alert('Pasted image must be under 2MB');
                        img.remove();
                        continue;
                    }
                    const { url } = await imageApi.upload(file, 'paste');
                    img.setAttribute('src', url);
                } catch (e) {
                    console.error('Failed to upload pasted image', e);
                    // If upload fails, remove the heavy data image to prevent DB bloat
                    img.remove();
                }
            }
        }
    };

    // Handle paste events to intercept images and upload to OCI, inserting URLs only
    const handlePaste: React.ClipboardEventHandler<HTMLDivElement> = async (e) => {
        const el = editorRef.current;
        if (!el) return;
        const dt = e.clipboardData;
        if (!dt) return;

        // If there are image files in clipboard, upload and insert by URL
        const items = Array.from(dt.items || []);
        const imageItems = items.filter(it => it.kind === 'file' && it.type.startsWith('image/'));
        if (imageItems.length > 0) {
            e.preventDefault();
            for (const it of imageItems) {
                const file = it.getAsFile();
                if (!file) continue;
                try {
                    if (!isAllowedMime(file.type)) {
                        alert(`Only ${ALLOWED_LABEL} images are allowed`);
                        continue;
                    }
                    if (file.size > MAX_PASTE) {
                        alert('Pasted image must be under 2MB');
                        continue;
                    }
                    const { url } = await imageApi.upload(file, 'paste');
                    // Insert image at caret
                    document.execCommand('insertImage', false, url);
                } catch (err) {
                    console.error('Image upload failed', err);
                    alert('Failed to upload pasted image');
                }
            }
            // Update model
            onChange({ questionRichText: el.innerHTML });
            return;
        }

        // If HTML is being pasted, scrub data images
        const html = dt.getData('text/html');
        if (html) {
            e.preventDefault();
            const container = document.createElement('div');
            container.innerHTML = html;
            // Replace any data images within the pasted HTML
            const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
            for (const img of imgs) {
                const src = img.getAttribute('src') || '';
                if (src.startsWith('data:image')) {
                    try {
                        const file = await dataUrlToFile(src, `pasted-${Date.now()}.png`);
                        if (!isAllowedMime(file.type)) {
                            alert(`Only ${ALLOWED_LABEL} images are allowed`);
                            img.remove();
                            continue;
                        }
                        if (file.size > MAX_PASTE) {
                            alert('Pasted image must be under 2MB');
                            img.remove();
                            continue;
                        }
                        const { url } = await imageApi.upload(file, 'paste');
                        img.setAttribute('src', url);
                    } catch (err) {
                        console.error('Pasted HTML image upload failed', err);
                        img.remove();
                    }
                }
            }
            document.execCommand('insertHTML', false, container.innerHTML);
            onChange({ questionRichText: el.innerHTML });
            return;
        }

        // Fallback: plain text
        const text = dt.getData('text/plain');
        if (text) {
            e.preventDefault();
            document.execCommand('insertText', false, text);
            onChange({ questionRichText: el.innerHTML });
        }
    };

    // Ensure defaults are applied silently when hidden
    React.useEffect(() => {
        if (!showSubjectClass) {
            const patch: Partial<Question> = {};
            if (defaultSubject && !question.subject) patch.subject = defaultSubject;
            if (defaultClassLevel && !question.classLevel) patch.classLevel = defaultClassLevel;
            if (Object.keys(patch).length > 0) onChange(patch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSubjectClass, defaultSubject, defaultClassLevel]);

    const toggleCorrect = (ansIndex: number) => {
        if (question.questionType !== 'MCQ') return;
        const updated = (question.answers || []).map((a, i) => ({ ...a, isCorrect: i === ansIndex }));
        onChange({ answers: updated });
    };

    const addAnswer = () => {
        if (question.questionType !== 'MCQ') return;
        const updated = [ ...(question.answers || []), { answerText: '', isCorrect: false } as Answer ];
        onChange({ answers: updated });
    };
    const removeAnswer = (i: number) => {
        if (question.questionType !== 'MCQ') return;
        const updated = (question.answers || []).filter((_, idx) => idx !== i);
        onChange({ answers: updated });
    };

    // Complexity & Tags helpers (used when showComplexityTags = true)
    const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const value = tagInput.trim();
            if (!value) return;
            const current = new Set(question.tags || []);
            current.add(value);
            onChange({ tags: Array.from(current) });
            setTagInput('');
        }
        if (e.key === 'Backspace' && !tagInput && (question.tags || []).length > 0) {
            const copy = [ ...(question.tags || []) ];
            copy.pop();
            onChange({ tags: copy });
        }
    };

    const removeTag = (tag: string) => {
        const remaining = (question.tags || []).filter(t => t !== tag);
        onChange({ tags: remaining });
    };

    return (
        <div style={{ width: '100%' }}>
            {showSubjectClass && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                        <label className="form-label">Subject</label>
                        <select
                            className="form-input"
                            value={question.subject || ''}
                            onChange={(e) => onChange({ subject: e.target.value })}
                        >
                            <option value="" disabled>Select subject</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.name}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Class</label>
                        <select
                            className="form-input"
                            value={question.classLevel || ''}
                            onChange={(e) => onChange({ classLevel: e.target.value })}
                        >
                            <option value="" disabled>Select class</option>
                            {classes.map(cl => (
                                <option key={cl} value={cl}>{cl}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {showComplexityTags && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: 8 }}>
                    <div className="form-group">
                        <label className="form-label">Complexity</label>
                        <select className="form-input" value={question.complexity || ''} onChange={e => onChange({ complexity: (e.target.value || undefined) as any })}>
                            <option value="">Select complexity</option>
                            <option value="easy">easy</option>
                            <option value="medium">medium</option>
                            <option value="hard">hard</option>
                            <option value="very hard">very hard</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Tags</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, border: '1px solid #e2e8f0', borderRadius: 6, padding: 6 }}>
                            {(question.tags || []).map((t, i) => (
                                <span key={i} className="badge badge-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    {t}
                                    <button type="button" className="btn btn-xs" onClick={() => removeTag(t)}>×</button>
                                </span>
                            ))}
                            <input
                                className="form-input"
                                style={{ border: 'none', outline: 'none', flex: '1 1 120px', minWidth: 120 }}
                                placeholder="Type and press Enter to add tag"
                                value={tagInput}
                                onChange={e => setTagInput(e.target.value)}
                                onKeyDown={handleTagKeyDown}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="form-group" style={{ marginTop: (showSubjectClass || showComplexityTags) ? 8 : 0 }}>
                <label className="form-label">Type</label>
                <select
                    className="form-input"
                    value={question.questionType}
                    onChange={e => onChange({ questionType: e.target.value as QuestionType, answers: e.target.value === 'MCQ' ? (question.answers && question.answers.length > 0 ? question.answers : [
                        { answerText: '', isCorrect: false },
                        { answerText: '', isCorrect: false },
                        { answerText: '', isCorrect: false },
                        { answerText: '', isCorrect: true },
                    ]) : [] })}
                >
                    <option value="MCQ">MCQ</option>
                    <option value="SUBJECTIVE">SUBJECTIVE</option>
                </select>
            </div>

            <div className="form-group">
                <label className="form-label">Marks</label>
                <input
                    type="number"
                    min={1}
                    className="form-input"
                    value={question.marks}
                    onChange={e => onChange({ marks: Number(e.target.value || 0) })}
                />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <input id="imgPrimary" type="checkbox" checked={!!question.imageAsPrimary} onChange={e => onChange({ imageAsPrimary: e.target.checked })} />
                <label htmlFor="imgPrimary">Use image as primary content</label>
            </div>

            <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">Image</label>
                <input type="file" accept="image/*" onChange={async (e) => {
                    const inputEl = e.currentTarget;
                    const file = inputEl.files?.[0];
                    if (!file) { try { inputEl.value = ''; } catch {} return; }
                    try {
                        await uploadImage(file);
                    } catch {
                        alert('Failed to upload image');
                    } finally {
                        try { inputEl.value = ''; } catch {}
                    }
                }} />
                {question.imagePath && (
                    <div style={{ marginTop: 6 }}>
                        <img src={question.imagePath} alt="uploaded" style={{ maxHeight: 120, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                        <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                            <button type="button" className="btn btn-secondary btn-xs" onClick={() => setPreviewSrc(question.imagePath!)}>
                                Preview
                            </button>
                            <button type="button" className="btn btn-danger btn-xs" onClick={() => onChange({ imagePath: '' })}>Remove</button>
                        </div>
                        <PreviewImageModal src={previewSrc || undefined} open={!!previewSrc} onClose={() => setPreviewSrc(null)} alt="Question image preview" />
                    </div>
                )}
            </div>

            {!question.imageAsPrimary && (
                <>
                    <div className="form-group" style={{ marginTop: 8 }}>
                        <label className="form-label">Question Text</label>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                            <button type="button" className="btn btn-outline btn-sm" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }}>B</button>
                            <button type="button" className="btn btn-outline btn-sm" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }}>I</button>
                            <input type="color" title="Text Color" onMouseDown={(e) => e.preventDefault()} onChange={(e) => document.execCommand('foreColor', false, e.target.value)} />
                            <button type="button" className="btn btn-outline btn-sm" title="Insert Table (align digits)" onMouseDown={(e) => { e.preventDefault(); handleInsertTable(); }}>Table</button>
                            <button type="button" className="btn btn-outline btn-sm" title="Toggle Table Borders" onMouseDown={(e) => { e.preventDefault(); handleToggleBorders(); }}>Borders</button>
                            <select title="Font Size" onChange={(e) => { restoreSelection(); document.execCommand('fontSize', false, e.target.value); }}>
                                <option value="3">Normal</option>
                                <option value="4">Large</option>
                                <option value="5">Larger</option>
                                <option value="6">X-Large</option>
                            </select>
                            {/* Math toolbar */}
                            <span style={{ width: 1, background: '#e2e8f0', alignSelf: 'stretch' }} />
                            <button type="button" className="btn btn-outline btn-sm" title="Insert Fraction (e.g., 2/3 with horizontal bar)" onMouseDown={async (e) => {
                                e.preventDefault();
                                const bm = placeCaretBookmark();
                                const num = window.prompt('Numerator', '2');
                                if (num == null) return;
                                const den = window.prompt('Denominator', '3');
                                if (den == null) return;
                                editorRef.current?.focus();
                                restoreCaretFromBookmark(bm);
                                await insertMath(`\\frac{${num}}{${den}}`);
                            }}>a/b</button>
                            <button type="button" className="btn btn-outline btn-sm" title="Insert Power (no ^ needed)" onMouseDown={async (e) => {
                                e.preventDefault();
                                const bm = placeCaretBookmark();
                                const base = window.prompt('Base', '2');
                                if (base == null) return;
                                const exp = window.prompt('Exponent (expression allowed, e.g., a+b)', '(a+b)');
                                if (exp == null) return;
                                editorRef.current?.focus();
                                restoreCaretFromBookmark(bm);
                                await insertMath(`${base}^{${exp}}`);
                            }}>x^y</button>
                            <button type="button" className="btn btn-outline btn-sm" title="Square" onMouseDown={async (e) => { e.preventDefault(); const bm = placeCaretBookmark(); const b = window.prompt('Base', 'x'); if (b == null) return; editorRef.current?.focus(); restoreCaretFromBookmark(bm); await insertMath(`${b}^{2}`); }}>x²</button>
                            <button type="button" className="btn btn-outline btn-sm" title="Cube" onMouseDown={async (e) => { e.preventDefault(); const bm = placeCaretBookmark(); const b = window.prompt('Base', 'x'); if (b == null) return; editorRef.current?.focus(); restoreCaretFromBookmark(bm); await insertMath(`${b}^{3}`); }}>x³</button>
                            <button type="button" className="btn btn-outline btn-sm" title="Square Root" onMouseDown={async (e) => { e.preventDefault(); const bm = placeCaretBookmark(); const r = window.prompt('Radicand', 'x'); if (r == null) return; editorRef.current?.focus(); restoreCaretFromBookmark(bm); await insertMath(`\\sqrt{${r}}`); }}>√</button>
                            <button type="button" className="btn btn-outline btn-sm" title="Cube Root" onMouseDown={async (e) => { e.preventDefault(); const bm = placeCaretBookmark(); const r = window.prompt('Radicand', 'x'); if (r == null) return; editorRef.current?.focus(); restoreCaretFromBookmark(bm); await insertMath(`\\sqrt[3]{${r}}`); }}>∛</button>
                            <button type="button" className="btn btn-outline btn-sm" title="n-th Root (choose index)" onMouseDown={async (e) => {
                                e.preventDefault();
                                const bm = placeCaretBookmark();
                                const idx = window.prompt('Root index (e.g., 4 for 4th root)', '4');
                                if (idx == null) return;
                                const r = window.prompt('Radicand', 'x');
                                if (r == null) return;
                                editorRef.current?.focus();
                                restoreCaretFromBookmark(bm);
                                await insertMath(`\\sqrt[${idx}]{${r}}`);
                            }}>n√</button>
                            <button type="button" className="btn btn-outline btn-sm" onMouseDown={(e) => { e.preventDefault(); onChange({ questionRichText: '' , questionText: ''}); if (editorRef.current) editorRef.current.innerHTML = ''; }}>Clear</button>
                        </div>
                        <div
                            contentEditable
                            suppressContentEditableWarning
                            className="form-input"
                            style={{ minHeight: '80px' }}
                            ref={editorRef}
                            onKeyUp={saveSelection}
                            onMouseUp={saveSelection}
                            onPaste={handlePaste}
                            onBlur={async (e) => {
                                const el = e.target as HTMLDivElement;
                                await replaceDataImagesInElement(el);
                                const html = el.innerHTML;
                                const plain = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                                onChange({ questionRichText: html, questionText: plain });
                            }}
                        />
                        <small>Type normally or use the toolbar for formatting and math (fraction, powers, roots). Example: insert 2/(a+b), x², √x, ³√x or ⁴√x (n-th root) with the Math buttons.</small>
                    </div>
                </>
            )}

            {/* Shapes editor is only available in CreateExam today; include here for parity */}
            {showShapesEditor && (
            <div className="form-group">
                <label className="form-label">Geometric Shapes (optional)</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                        const shapes = JSON.parse(question.shapesJson || '[]');
                        const n = shapes.length;
                        const cx = 20 + ((n * 10) % 60);
                        const cy = 20 + ((n * 8) % 40);
                        shapes.push({ type: 'circle', cx, cy, r: 12, stroke: '#333', fill: '#e2e8f0' });
                        onChange({ shapesJson: JSON.stringify(shapes) });
                    }}>Add Circle</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                        const shapes = JSON.parse(question.shapesJson || '[]');
                        const n = shapes.length;
                        const x = 10 + ((n * 10) % 60);
                        const y = 10 + ((n * 8) % 40);
                        shapes.push({ type: 'rect', x, y, width: 30, height: 18, stroke: '#333', fill: '#e2e8f0' });
                        onChange({ shapesJson: JSON.stringify(shapes) });
                    }}>Add Rectangle</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                        const shapes = JSON.parse(question.shapesJson || '[]');
                        const n = shapes.length;
                        const x = 15 + ((n * 10) % 60);
                        const y = 15 + ((n * 8) % 40);
                        shapes.push({ type: 'square', x, y, size: 20, stroke: '#333', fill: '#e2e8f0' });
                        onChange({ shapesJson: JSON.stringify(shapes) });
                    }}>Add Square</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                        const shapes = JSON.parse(question.shapesJson || '[]');
                        const n = shapes.length;
                        const ox = 10 + ((n * 10) % 60);
                        const oy = 10 + ((n * 6) % 40);
                        const points = [
                            `${ox},${oy}`,
                            `${ox + 40},${oy}`,
                            `${ox + 30},${oy + 30}`,
                            `${ox + 10},${oy + 30}`,
                        ].join(' ');
                        shapes.push({ type: 'trapezium', points, stroke: '#333', fill: '#e2e8f0' });
                        onChange({ shapesJson: JSON.stringify(shapes) });
                    }}>Add Trapezium</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => onChange({ shapesJson: '[]' })}>Clear Shapes</button>
                </div>
                <div style={{ border: '1px dashed #cbd5e0', padding: '8px', borderRadius: '8px' }}>
                    <svg
                        viewBox="0 0 100 80"
                        width="100%"
                        height="160"
                        style={{ cursor: 'default' }}
                        onMouseMove={(e) => {
                            // Simple drag based on last mousedown stored on element dataset
                            const target = e.currentTarget as SVGSVGElement;
                            const ds: any = (target as any)._dragState;
                            if (!ds) return;
                            const rect = target.getBoundingClientRect();
                            const x = ((e.clientX - rect.left) / rect.width) * 100;
                            const y = ((e.clientY - rect.top) / rect.height) * 80;
                            const dx = x - ds.startX;
                            const dy = y - ds.startY;
                            const shapes = JSON.parse(question.shapesJson || '[]');
                            const idx = ds.index;
                            const s = { ...shapes[idx] };
                            if (ds.type === 'circle') { s.cx = (ds.orig.cx || 0) + dx; s.cy = (ds.orig.cy || 0) + dy; }
                            if (ds.type === 'rect') { s.x = (ds.orig.x || 0) + dx; s.y = (ds.orig.y || 0) + dy; }
                            if (ds.type === 'square') { s.x = (ds.orig.x || 0) + dx; s.y = (ds.orig.y || 0) + dy; }
                            if (ds.type === 'trapezium') {
                                const moved = ds.origPoints.map((pt: any) => ({ x: pt.x + dx, y: pt.y + dy }));
                                s.points = moved.map((pt: any) => `${pt.x},${pt.y}`).join(' ');
                            }
                            shapes[idx] = s;
                            onChange({ shapesJson: JSON.stringify(shapes) });
                        }}
                        onMouseUp={(e) => { const svg = e.currentTarget as any; svg._dragState = null; }}
                        onMouseLeave={(e) => { const svg = e.currentTarget as any; svg._dragState = null; }}
                    >
                        {(JSON.parse(question.shapesJson || '[]') as any[]).map((s, i) => {
                            if (s.type === 'circle') return (
                                <circle key={i} cx={s.cx} cy={s.cy} r={s.r} stroke={s.stroke} fill={s.fill}
                                    style={{ cursor: 'move' }}
                                    onMouseDown={(e) => {
                                        const svg = (e.currentTarget as any).ownerSVGElement as any;
                                        const rect = svg.getBoundingClientRect();
                                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                                        const y = ((e.clientY - rect.top) / rect.height) * 80;
                                        svg._dragState = { index: i, type: 'circle', startX: x, startY: y, orig: { ...s } };
                                    }} />
                            );
                            if (s.type === 'rect') return (
                                <rect key={i} x={s.x} y={s.y} width={s.width} height={s.height} stroke={s.stroke} fill={s.fill}
                                    style={{ cursor: 'move' }}
                                    onMouseDown={(e) => {
                                        const svg = (e.currentTarget as any).ownerSVGElement as any;
                                        const rect = svg.getBoundingClientRect();
                                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                                        const y = ((e.clientY - rect.top) / rect.height) * 80;
                                        svg._dragState = { index: i, type: 'rect', startX: x, startY: y, orig: { ...s } };
                                    }} />
                            );
                            if (s.type === 'square') return (
                                <rect key={i} x={s.x} y={s.y} width={s.size} height={s.size} stroke={s.stroke} fill={s.fill}
                                    style={{ cursor: 'move' }}
                                    onMouseDown={(e) => {
                                        const svg = (e.currentTarget as any).ownerSVGElement as any;
                                        const rect = svg.getBoundingClientRect();
                                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                                        const y = ((e.clientY - rect.top) / rect.height) * 80;
                                        svg._dragState = { index: i, type: 'square', startX: x, startY: y, orig: { ...s } };
                                    }} />
                            );
                            if (s.type === 'trapezium') return (
                                <polygon key={i} points={s.points} stroke={s.stroke} fill={s.fill}
                                    style={{ cursor: 'move' }}
                                    onMouseDown={(e) => {
                                        const svg = (e.currentTarget as any).ownerSVGElement as any;
                                        const rect = svg.getBoundingClientRect();
                                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                                        const y = ((e.clientY - rect.top) / rect.height) * 80;
                                        const pts = String(s.points).split(/\s+/).map((p: string) => { const [px, py] = p.split(',').map((n: string) => parseFloat(n)); return { x: px, y: py }; });
                                        svg._dragState = { index: i, type: 'trapezium', startX: x, startY: y, orig: { ...s }, origPoints: pts };
                                    }} />
                            );
                            return null;
                        })}
                    </svg>
                </div>
            </div>
            )}

            {question.questionType === 'MCQ' && (
                <div style={{ marginTop: 8 }}>
                    <label className="form-label">Options</label>
                    {(question.answers || []).map((ans, i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 6,
                                padding: '4px 6px',
                                borderRadius: 6,
                                backgroundColor: ans.isCorrect ? '#e6fffa' : undefined,
                                border: ans.isCorrect ? '1px solid #38b2ac' : '1px solid transparent',
                            }}
                            title={ans.isCorrect ? 'Correct answer' : 'Mark as correct'}
                        >
                            <input
                                type="radio"
                                name={radioGroupNameRef.current}
                                checked={!!ans.isCorrect}
                                onChange={() => toggleCorrect(i)}
                            />
                            <input
                                className="form-input"
                                value={ans.answerText}
                                onChange={e => {
                                    const updated = (question.answers || []).map((a, idx) => idx === i ? { ...a, answerText: e.target.value } : a);
                                    onChange({ answers: updated });
                                }}
                            />
                            <button className="btn btn-xs" onClick={() => removeAnswer(i)}>🗑</button>
                        </div>
                    ))}
                    <button className="btn btn-sm" onClick={addAnswer}>➕ Add Option</button>
                </div>
            )}
        </div>
    );
};

export default QuestionEditor;
